import React from 'react';
import { TextField, InputAdornment, Box } from '@mui/material';
import { useTouchModeEnabled } from '@stores/calibrationUIStore';
import {
  TouchModeInput,
  useTouchModeActions
} from '@features/calibration/components/shared';
import { useInputFieldState } from '@features/calibration/components/shared/useInputFieldState';
import { getEditableInputDataAttributes } from '@features/calibration/utils/editableInputDataAttributes';
import type { CalibrationEditableCellProps } from './types';

export const EditableInput: React.FC<CalibrationEditableCellProps> = ({
  field,
  value,
  point,
  readability,
  resolution,
  unit,
  onUpdate,
  testType,
  extraInputProps = {},
  isDisabled = false
}) => {
  const touchModeEnabledSetting = useTouchModeEnabled();
  const touchModeEnabled = touchModeEnabledSetting && !isDisabled;

  const {
    handleFocus,
    handleChange,
    handleBlur,
    handleKeyDown,
    getFormattedDisplayValue
  } = useInputFieldState();

  const { incrementValue, decrementValue } = useTouchModeActions(
    point,
    readability
  );

  const wrappedOnUpdate = (
    _field: string,
    updateValue: string | number | null,
    options?: { immediate?: boolean }
  ) => {
    onUpdate(field, updateValue, options);
  };

  const displayValue = getFormattedDisplayValue(
    value,
    field,
    resolution,
    point.id
  );

  const handleIncrement = () => incrementValue(field, wrappedOnUpdate);
  const handleDecrement = () => decrementValue(field, wrappedOnUpdate);

  const dataAttributes = getEditableInputDataAttributes(testType, point, field);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5
      }}
    >
      {touchModeEnabled ? (
        <TouchModeInput
          field={field}
          value={displayValue}
          onChange={(f, v) => handleChange(f, v, point.id)}
          onFocus={handleFocus}
          onBlur={() =>
            handleBlur(
              field,
              wrappedOnUpdate,
              resolution,
              readability,
              point.id
            )
          }
          onKeyDown={(touchField, e) =>
            handleKeyDown(
              touchField,
              e,
              wrappedOnUpdate,
              resolution,
              readability,
              point.id
            )
          }
          onIncrement={handleIncrement}
          onDecrement={handleDecrement}
          unit={unit}
          extraInputProps={{
            ...dataAttributes,
            ...extraInputProps
          }}
          disabled={isDisabled}
        />
      ) : (
        <TextField
          size="small"
          fullWidth
          value={displayValue}
          onChange={e => handleChange(field, e.target.value, point.id)}
          onFocus={e => handleFocus(field, e)}
          onBlur={() =>
            handleBlur(
              field,
              wrappedOnUpdate,
              resolution,
              readability,
              point.id
            )
          }
          onKeyDown={e =>
            handleKeyDown(
              field,
              e,
              wrappedOnUpdate,
              resolution,
              readability,
              point.id
            )
          }
          disabled={isDisabled}
          sx={theme => ({
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: theme.palette.text.primary,
              color: theme.palette.text.primary
            },
            '& .MuiInputAdornment-root.Mui-disabled': {
              color: theme.palette.text.secondary
            }
          })}
          inputProps={{
            inputMode: 'decimal',
            ...dataAttributes,
            ...extraInputProps,
            style: { textAlign: 'right' }
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">{unit}</InputAdornment>
          }}
        />
      )}
    </Box>
  );
};
